import { createReducer } from 'redux-act';
import { TErrors } from '@alycecom/services';
import { propOr } from 'ramda';

import { BgOptions, BrandingImageName, TBrandingImage } from '../types/Branding.types';
import { IBranding, BrandingFields } from '../../../types/Branding.types';
import { getInitialBackgroundOption } from '../helpers/branding.helpers';
import { getBrandingColor } from '../../../helpers/branding.helpers';
import { defaultBrandingColors } from '../../../constants/branding.constants';

import {
  resetBrandingChanges,
  setBackgroundOption,
  setupInitialBranding,
  updateBrandingFail,
  updateBrandingRequest,
  updateBrandingSuccess,
  uploadImageFail,
  uploadImageRequest,
  uploadImageSuccess,
} from './branding.actions';

export interface IBrandingState {
  isLoading: boolean;
  branding?: IBranding;
  background: BgOptions;
  logo: TBrandingImage;
  backgroundImage: TBrandingImage;
  errors: TErrors;
}

export const initialState: IBrandingState = {
  isLoading: false,
  branding: undefined,
  background: BgOptions.empty,
  [BrandingImageName.logo]: {
    id: 0,
    url: '',
  },
  [BrandingImageName.backgroundImage]: {
    id: 0,
    url: '',
  },
  errors: {},
};

const reducer = createReducer<IBrandingState>({}, initialState);

reducer
  .on(uploadImageRequest, state => ({
    ...state,
    isLoading: true,
  }))
  .on(uploadImageSuccess, (state, { id, name, url }) => ({
    ...state,
    isLoading: false,
    isLoaded: true,
    [name]: {
      id,
      url,
    },
  }))
  .on(uploadImageFail, (state, payload) => ({
    ...state,
    isLoading: false,
    errors: payload,
  }));

reducer
  .on(updateBrandingRequest, state => ({
    ...state,
    isLoading: true,
  }))
  .on(updateBrandingSuccess, (state, payload) => ({
    ...state,
    isLoading: false,
    branding: {
      ...payload,
      [BrandingFields.termsUrl]: payload[BrandingFields.termsUrl] || '',
    },
  }))
  .on(updateBrandingFail, (state, payload) => ({
    ...state,
    isLoading: false,
    errors: payload,
  }));

reducer.on(setBackgroundOption, (state, payload) => ({
  ...state,
  background: payload,
}));

reducer.on(setupInitialBranding, (state, payload) => ({
  ...state,
  branding: {
    ...payload,
    backgroundColor: getBrandingColor(payload.backgroundColor, defaultBrandingColors.backgroundColor),
    primaryColor: getBrandingColor(payload.primaryColor, defaultBrandingColors.primaryColor),
    secondaryColor: getBrandingColor(payload.secondaryColor, defaultBrandingColors.secondaryColor),
    tertiaryColor: getBrandingColor(payload.tertiaryColor, defaultBrandingColors.tertiaryColor),
    highlightColor: getBrandingColor(payload.highlightColor, defaultBrandingColors.highlightColor),
    headlineColor: getBrandingColor(payload.headlineColor, defaultBrandingColors.headlineColor),
    primaryCtaColor: getBrandingColor(payload.primaryCtaColor, defaultBrandingColors.primaryCtaColor),
    secondaryCtaColor: getBrandingColor(payload.secondaryCtaColor, defaultBrandingColors.secondaryCtaColor),
    [BrandingFields.termsUrl]: payload[BrandingFields.termsUrl] || '',
  },
  [BrandingImageName.logo]: {
    id: payload.logoId,
    url: propOr('', 'logoUrl', payload),
  },
  [BrandingImageName.backgroundImage]: {
    id: payload.backgroundImageId,
    url: propOr('', 'backgroundImageUrl', payload),
  },
  background: getInitialBackgroundOption({
    backgroundImageUrl: propOr('', 'backgroundImageUrl', payload),
    backgroundColor: payload.backgroundColor,
    tertiaryColor: payload.tertiaryColor,
  }),
}));

reducer.on(resetBrandingChanges, (state, payload) => ({
  ...state,
  [BrandingImageName.logo]: {
    id: payload[BrandingFields.logoId] || 0,
    url: payload[BrandingFields.logoUrl] || '',
  },
  [BrandingImageName.backgroundImage]: {
    id: payload[BrandingFields.backgroundImageId] || 0,
    url: payload[BrandingFields.backgroundImageUrl] || '',
  },
  background: getInitialBackgroundOption({
    backgroundImageUrl: payload[BrandingFields.backgroundImageUrl],
    backgroundColor: payload[BrandingFields.backgroundColor],
    tertiaryColor: payload[BrandingFields.tertiaryColor],
  }),
}));

export default reducer;
